import { Component, OnInit, OnDestroy } from '@angular/core';
import { environment } from '../../environments/environment';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ImportadorService, ImportadorConsultarResponse } from '../services/importador.service';
import { EventService } from '../services/event.service';
import { HttpErrorResponse } from '@angular/common/http';
import moment from 'moment';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ToastrService } from 'ngx-toastr';
import { ModalImportarComponent } from './modal-importar/modal-importar.component';
import { Subscription, interval } from 'rxjs';

@Component({
  selector: 'app-importador',
  standalone: true,
  imports: [CommonModule, FormsModule, BsDatepickerModule, TooltipModule],
  templateUrl: './importador.component.html',
  styleUrl: './importador.component.scss'
})
export class ImportadorComponent implements OnInit, OnDestroy {

  registros: any[] = [];
  registrosFiltrados: any[] = [];
  cargando: boolean = false;
  fechaSeleccionada: Date = null;

  page: number = 0;
  pageSize: number = 15;
  totalPages: number = 0;

  columnas: string[] = [];
  private eventSub: Subscription;

  private pollingSub: Subscription;
  private readonly INTERVALO_POLLING_MS = 5000;
  private readonly ESTADOS_EN_CURSO = ['en_curso', 'eliminacion_en_curso'];

  constructor(
    private importadorService: ImportadorService,
    private toastr: ToastrService,
    private modalService: BsModalService,
    private eventService: EventService
  ) {}

  ngOnInit(): void {
    this.consultar();
    this.eventSub = this.eventService.subscribe('IMPORTADOR_EJECUTADO', () => {
      this.consultar();
    });
  }

  ngOnDestroy(): void {
    if (this.eventSub) {
      this.eventSub.unsubscribe();
    }
    this.detenerPolling();
  }

  consultar() {
    this.cargando = true;
    this.detenerPolling();
    const fecha = this.fechaSeleccionada ? moment(this.fechaSeleccionada).format('YYYY-MM-DD') : null;

    this.importadorService.consultar(fecha).subscribe(
      (resp: ImportadorConsultarResponse) => {
        this.cargando = false;
        if (resp.success) {
          this.registros = (resp.data || []).sort((a, b) => {
            const fechaA = a.fechaCreacion || a.fecha || 0;
            const fechaB = b.fechaCreacion || b.fecha || 0;
            return fechaB - fechaA;
          });
          this.page = 0;
          this.calcularPaginacion();
          this.extraerColumnas();
          this.iniciarPollingSiCorresponde();
        } else {
          this.toastr.warning(resp.msg, 'Aviso', { timeOut: environment.toastWarningTimeout, extendedTimeOut: environment.toastWarningTimeout, closeButton: true, tapToDismiss: false });
        }
      },
      (error: HttpErrorResponse) => {
        this.cargando = false;
        this.toastr.error((error.error?.msg || 'Error al consultar importador') + (error['requestId'] ? ' Code: ' + error['requestId'] : ''), 'Error', { timeOut: environment.toastErrorTimeout, extendedTimeOut: environment.toastErrorTimeout, closeButton: true, tapToDismiss: false });
      }
    );
  }

  private hayFilasEnCurso(): boolean {
    return this.registros.some(r => this.ESTADOS_EN_CURSO.includes((r.estado || '').toString().toLowerCase()));
  }

  private iniciarPollingSiCorresponde() {
    if (this.pollingSub) return;
    if (!this.hayFilasEnCurso()) return;
    this.pollingSub = interval(this.INTERVALO_POLLING_MS).subscribe(() => this.refrescarFilasEnCurso());
  }

  private detenerPolling() {
    if (this.pollingSub) {
      this.pollingSub.unsubscribe();
      this.pollingSub = null;
    }
  }

  private refrescarFilasEnCurso() {
    if (!this.hayFilasEnCurso()) {
      this.detenerPolling();
      return;
    }

    const fecha = this.fechaSeleccionada ? moment(this.fechaSeleccionada).format('YYYY-MM-DD') : null;

    this.importadorService.consultar(fecha).subscribe(
      (resp: ImportadorConsultarResponse) => {
        if (!resp || !resp.success || !resp.data) return;

        const indexEntrante = new Map<any, any>();
        for (const incoming of resp.data) {
          const key = incoming.id ?? incoming.uuid;
          if (key !== undefined && key !== null) indexEntrante.set(key, incoming);
        }

        for (const fila of this.registros) {
          const estadoLocal = (fila.estado || '').toString().toLowerCase();
          if (!this.ESTADOS_EN_CURSO.includes(estadoLocal)) continue;
          const key = fila.id ?? fila.uuid;
          const incoming = indexEntrante.get(key);
          if (!incoming) continue;
          for (const k of Object.keys(incoming)) {
            if (k === 'id' || k === 'uuid') continue;
            fila[k] = incoming[k];
          }
        }

        if (!this.hayFilasEnCurso()) this.detenerPolling();
      },
      () => { /* silencioso: se reintenta en el próximo tick */ }
    );
  }

  // CON-1049: se muestra "cantidad errores" en vez de "mensaje error". El número es cuántas líneas
  // ERROR trae el log de la importación (viajes que no se crearon, jornadas con falla, etc.), así se
  // ve de inmediato si hay algo que revisar sin tener que abrir el log y leerlo línea por línea.
  // mensajeError se sigue recibiendo y se muestra como tooltip sobre el estado: solo viene cuando la
  // importación se cayó entera, y ahí es el motivo.
  private columnasOcultas = ['uuid', 'id', 'mensajeError', 'borrado', 'creadoPorUsuario', 'borradoPorUsuario', 'fechaBorrado', 'borradoFecha', 'version'];
  private columnasPrioritarias = ['fechaImportar', 'fechaAImportar', 'fecha', 'fechaCreacion'];

  extraerColumnas() {
    if (this.registros.length > 0) {
      const todas = Object.keys(this.registros[0]).filter(k => !this.columnasOcultas.includes(k));
      const primero: string[] = [];
      const resto: string[] = [];
      for (const col of todas) {
        if (this.columnasPrioritarias.includes(col)) {
          primero.push(col);
        } else {
          resto.push(col);
        }
      }
      primero.sort((a, b) => this.columnasPrioritarias.indexOf(a) - this.columnasPrioritarias.indexOf(b));
      this.columnas = [...primero, ...resto];
      // El orden del resto lo define el orden de los campos en la respuesta del backend, que no es
      // un criterio de pantalla. Las que importan se ubican a mano. Ver CON-1049.
      this.moverDespuesDe('cantidadErrores', 'origen');
    } else {
      this.columnas = [];
    }
  }

  /**
   * Deja `columna` inmediatamente después de `referencia`. Si alguna de las dos no está presente
   * (el backend dejó de enviarla, o quedó oculta), no hace nada y el orden queda como venía.
   */
  private moverDespuesDe(columna: string, referencia: string) {
    const desde = this.columnas.indexOf(columna);
    if (desde === -1) return;
    const hasta = this.columnas.indexOf(referencia);
    if (hasta === -1) return;

    this.columnas.splice(desde, 1);
    // El índice de la referencia puede haberse corrido al sacar la columna de su posición previa.
    this.columnas.splice(this.columnas.indexOf(referencia) + 1, 0, columna);
  }

  calcularPaginacion() {
    this.totalPages = Math.ceil(this.registros.length / this.pageSize) || 1;
    this.actualizarPagina();
  }

  actualizarPagina() {
    const inicio = this.page * this.pageSize;
    this.registrosFiltrados = this.registros.slice(inicio, inicio + this.pageSize);
  }

  paginaAnterior() {
    if (this.page > 0) {
      this.page--;
      this.actualizarPagina();
    }
  }

  paginaSiguiente() {
    if (this.page < this.totalPages - 1) {
      this.page++;
      this.actualizarPagina();
    }
  }

  onChangeFecha(fecha: Date) {
    this.fechaSeleccionada = fecha || null;
    this.consultar();
  }

  esFechaPasada(): boolean {
    if (!this.fechaSeleccionada) return false;
    return moment(this.fechaSeleccionada).isBefore(moment(), 'day');
  }

  puedeEliminar(registro: any): boolean {
    if (this.esFechaPasada()) return false;
    const fechaRegistro = registro.fechaImportar || registro.fechaAImportar || registro.fecha;
    if (fechaRegistro) {
      let fechaMoment: moment.Moment;
      if (Array.isArray(fechaRegistro) && fechaRegistro.length === 3) {
        fechaMoment = moment({ year: fechaRegistro[0], month: fechaRegistro[1] - 1, day: fechaRegistro[2] });
      } else if (typeof fechaRegistro === 'number') {
        const ts = fechaRegistro > 9999999999 ? fechaRegistro : fechaRegistro * 1000;
        fechaMoment = moment(ts);
      } else {
        fechaMoment = moment(fechaRegistro);
      }
      if (fechaMoment.isValid() && fechaMoment.isBefore(moment(), 'day')) return false;
    }
    if (registro.borrado === true) return false;
    const estado = (registro.estado || '').toLowerCase();
    return estado !== 'eliminado';
  }

  registroPendienteBorrar: any = null;

  confirmarEliminar(registro: any) {
    this.registroPendienteBorrar = registro;
  }

  cancelarEliminar() {
    this.registroPendienteBorrar = null;
  }

  eliminar() {
    const registro = this.registroPendienteBorrar;
    if (!registro) return;
    const id = registro.id;
    if (!id) return;

    this.importadorService.borrar(id).subscribe(
      (resp: any) => {
        if (resp.success) {
          registro.estado = 'eliminado';
          registro.borrado = true;
          this.registroPendienteBorrar = null;
          this.toastr.success(resp.msg || 'Registro eliminado', 'Éxito', { timeOut: environment.toastSuccessTimeout, extendedTimeOut: environment.toastSuccessTimeout, closeButton: true, tapToDismiss: false });
        } else {
          this.toastr.warning(resp.msg, 'Aviso', { timeOut: environment.toastWarningTimeout, extendedTimeOut: environment.toastWarningTimeout, closeButton: true, tapToDismiss: false });
        }
      },
      (error: HttpErrorResponse) => {
        this.registroPendienteBorrar = null;
        this.toastr.error((error.error?.msg || 'Error al eliminar registro') + (error['requestId'] ? ' Code: ' + error['requestId'] : ''), 'Error', { timeOut: environment.toastErrorTimeout, extendedTimeOut: environment.toastErrorTimeout, closeButton: true, tapToDismiss: false });
      }
    );
  }

  verLogs(registro: any) {
    const id = registro.id;
    if (!id) {
      this.toastr.warning('Registro sin ID', 'Aviso', { timeOut: environment.toastWarningTimeout, extendedTimeOut: environment.toastWarningTimeout, closeButton: true, tapToDismiss: false });
      return;
    }
    this.importadorService.obtenerLogs(id).subscribe(
      (texto: string) => {
        const blob = new Blob([texto], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      },
      (error: HttpErrorResponse) => {
        this.toastr.error((error.error?.msg || 'Error al obtener logs') + (error['requestId'] ? ' Code: ' + error['requestId'] : ''), 'Error', { timeOut: environment.toastErrorTimeout, extendedTimeOut: environment.toastErrorTimeout, closeButton: true, tapToDismiss: false });
      }
    );
  }

  importar() {
    this.modalService.show(ModalImportarComponent, {
      ignoreBackdropClick: false,
      keyboard: true,
      class: 'modal-dialog-centered modal-xs'
    });
  }

  formatValue(value: any): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'SI' : 'NO';
    if (Array.isArray(value) && value.length === 3) {
      return moment({ year: value[0], month: value[1] - 1, day: value[2] }).format('DD-MM-YYYY');
    }
    if (typeof value === 'number' && value > 1000000000 && value < 9999999999999) {
      const ts = value > 9999999999 ? value : value * 1000;
      return moment(ts).format('DD-MM-YYYY HH:mm:ss');
    }
    return String(value);
  }

  formatHeader(key: string): string {
    return key.replace(/([A-Z])/g, ' $1').toUpperCase().trim();
  }

  // AUTOMATICA se destaca en azul; MANUAL y cualquier origen nuevo quedan en tono neutro
  claseOrigen(valor: string): string {
    return valor === 'AUTOMATICA' ? 'origenAutomatica' : 'origenNeutro';
  }
}
